import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, AlertCircle, UserCheck, UserX, BellRing } from 'lucide-react';
import { toast } from 'react-toastify';

import { Button } from '../../ui-v2';
import { Modal, Input, Tabs, Tab, ChipSelect, Badge } from '../../ui-v2.5';
import {
  getNegocios, getDepartamentos, getEmpresasContratantes, getEmpresasColaboradoras,
  previsualizarLote, crearLote,
} from '../api';
import SelectorPersonas from '../wizard/SelectorPersonas';

/**
 * ModalNuevoLote — añadir un lote a una campaña en curso (Activa o Pausada), en DS v2.
 *
 *   1. criterio: negocio, departamento (por NOMBRE, como el asistente), empresa (con «Sin empresa»)
 *      o selección manual; en campañas de colaboradoras, empresa colaboradora o manual;
 *   2. previsualizar: quién entra y quién no, y por qué («ya está en la campaña, en el Lote N»,
 *      inactiva…). No escribe nada;
 *   3. crear: se manda la lista CONFIRMADA (las que entraban en la previsualización). El servidor
 *      vuelve a comprobarlo todo; si algo cambió entre medias (otra alta, un cargo), lo dice y hay que
 *      volver a previsualizar.
 *
 * El ámbito lo decide la campaña, nunca este formulario. Todo el texto se pinta como texto.
 */

const MOTIVO = {
  ya_en_campana: 'Ya están en la campaña',
  inactiva: 'Inactivas',
  no_existe: 'No existen en este ámbito',
  id_invalido: 'Identificador no válido',
};
const MAX_NOMBRES = 12;

export default function ModalNuevoLote({ open, campana, onClose, onCreado }) {
  const colab = campana.ambito === 'colaborador';
  const tipos = colab
    ? [{ value: 'empresa_colaboradora', label: 'Empresa colaboradora' }, { value: 'manual', label: 'Selección manual' }]
    : [
      { value: 'negocio', label: 'Negocio' },
      { value: 'departamento', label: 'Departamento' },
      { value: 'empresa', label: 'Empresa' },
      { value: 'manual', label: 'Selección manual' },
    ];

  const [nombre, setNombre] = useState('');
  const [tipo, setTipo] = useState(tipos[0].value);
  const [valores, setValores] = useState([]);
  const [manual, setManual] = useState({ ids: [], cache: {} });
  const [opciones, setOpciones] = useState({});
  const [cargandoOpciones, setCargandoOpciones] = useState(false);
  const [preview, setPreview] = useState(null);
  const [trabajando, setTrabajando] = useState(false);
  const [error, setError] = useState(null);

  // Opciones del criterio elegido (una vez por tipo).
  useEffect(() => {
    if (tipo === 'manual' || opciones[tipo]) return;
    let vivo = true;
    setCargandoOpciones(true);
    const cargar = {
      negocio: async () => (await getNegocios()).map((n) => ({ value: n.id, label: n.nombre })),
      departamento: async () => (await getDepartamentos()).map((d) => ({ value: d.nombre, label: d.nombre })),
      empresa: async () => (await getEmpresasContratantes()).map((e) => ({ value: e.id, label: `${e.nombre} (${e.total})` })),
      empresa_colaboradora: async () => (await getEmpresasColaboradoras()).map((e) => ({ value: e.id, label: e.nombre })),
    }[tipo];
    cargar()
      .then((ops) => { if (vivo) setOpciones((o) => ({ ...o, [tipo]: ops })); })
      .catch(() => { if (vivo) setError('No se pudieron cargar las opciones del criterio.'); })
      .finally(() => { if (vivo) setCargandoOpciones(false); });
    return () => { vivo = false; };
  }, [tipo, opciones]);

  // Cambiar el criterio invalida la previsualización.
  useEffect(() => { setPreview(null); setError(null); }, [tipo, valores, manual.ids]);

  const criterio = useMemo(
    () => ({ tipo, valores: tipo === 'manual' ? manual.ids : valores }),
    [tipo, valores, manual.ids]
  );
  const hayValores = criterio.valores.length > 0;

  function cambiarTipo(t) { setTipo(t); setValores([]); }

  async function previsualizar() {
    setTrabajando(true); setError(null);
    try {
      setPreview(await previsualizarLote(campana.id, criterio));
    } catch (e) {
      setError(e?.response?.data?.error || 'No se pudo previsualizar el lote.');
    } finally { setTrabajando(false); }
  }

  async function crear() {
    setTrabajando(true); setError(null);
    try {
      const r = await crearLote(campana.id, {
        nombre: nombre.trim() || undefined,
        criterio,
        persona_ids: preview.incluidas.map((p) => p.persona_id),
      });
      toast.success(r.notificadas > 0
        ? `Lote ${r.lote.numero} creado con ${r.incluidas.length} personas. Se les ha avisado.`
        : `Lote ${r.lote.numero} creado con ${r.incluidas.length} personas.`);
      onCreado?.(r);
    } catch (e) {
      // Si algo cambió entre la previsualización y el alta, hay que volver a mirar.
      setPreview(null);
      setError(e?.response?.data?.error || 'No se pudo crear el lote.');
    } finally { setTrabajando(false); }
  }

  const porMotivo = useMemo(() => {
    const m = {};
    for (const e of preview?.excluidas || []) (m[e.motivo] = m[e.motivo] || []).push(e);
    return m;
  }, [preview]);

  return (
    <Modal
      open={open}
      onClose={trabajando ? () => {} : onClose}
      size="xl"
      title="Añadir un lote"
      subtitle={`Campaña ${campana.estado === 'Activa' ? 'activa: se avisará sólo a las personas nuevas' : 'pausada: se avisará al reanudarla'}`}
      footer={(
        <>
          <Button variant="outline" onClick={onClose} disabled={trabajando}>Cancelar</Button>
          {preview && preview.incluidas.length > 0 ? (
            <Button onClick={crear} disabled={trabajando}>
              {trabajando && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              Crear lote con {preview.incluidas.length} {preview.incluidas.length === 1 ? 'persona' : 'personas'}
            </Button>
          ) : (
            <Button onClick={previsualizar} disabled={trabajando || !hayValores}>
              {trabajando && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              Previsualizar
            </Button>
          )}
        </>
      )}
    >
      <div className="space-y-4">
        <Input
          label="Nombre del lote"
          hint="Opcional. Si lo dejas vacío se llamará «Lote N»."
          value={nombre}
          maxLength={120}
          onChange={(e) => setNombre(e.target.value)}
        />

        <div>
          <Tabs value={tipo} onChange={cambiarTipo}>
            {tipos.map((t) => <Tab key={t.value} value={t.value} label={t.label} />)}
          </Tabs>
          <div className="pt-3">
            {tipo === 'manual' ? (
              <SelectorPersonas
                rol="usuario"
                ambito={campana.ambito}
                excluirIds={[]}
                seleccionados={manual.ids}
                cache={manual.cache}
                onAdd={(p) => setManual((m) => (m.ids.includes(p.id) ? m : { ids: [...m.ids, p.id], cache: { ...m.cache, [p.id]: p } }))}
                onRemove={(id) => setManual((m) => ({ ...m, ids: m.ids.filter((x) => x !== id) }))}
                errores={[]}
                enAsistente={false}
                campanaId={campana.id}
              />
            ) : cargandoOpciones && !opciones[tipo] ? (
              <div className="text-sm text-v25-ink-500"><Loader2 className="w-4 h-4 inline-block animate-spin mr-2" />Cargando…</div>
            ) : (
              <ChipSelect options={opciones[tipo] || []} value={valores} onChange={setValores} multiple />
            )}
          </div>
        </div>

        {error && (
          <div className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-md p-3 flex items-start gap-2">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" /> {error}
          </div>
        )}

        {preview && (
          <div className="border border-v25-line rounded-v25-lg p-4 space-y-3">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <Badge tone="active" icon={UserCheck}>{preview.totales.incluidas} entran</Badge>
              {preview.totales.excluidas > 0 && (
                <Badge tone="waiting" icon={UserX}>{preview.totales.excluidas} quedan fuera</Badge>
              )}
              {preview.se_notificara && preview.incluidas.length > 0 && (
                <span className="text-xs text-v25-ink-500 flex items-center gap-1">
                  <BellRing className="w-3.5 h-3.5" /> Se avisará a {preview.incluidas.length} al crear el lote.
                </span>
              )}
            </div>

            {preview.incluidas.length === 0 && (
              <p className="text-sm text-v25-ink-700">
                Nadie entraría en este lote. No se puede crear un lote vacío: cambia el criterio.
              </p>
            )}

            <ListaNombres titulo="Entran" personas={preview.incluidas} />
            {Object.entries(porMotivo).map(([motivo, lista]) => (
              <ListaNombres
                key={motivo}
                titulo={MOTIVO[motivo] || motivo}
                personas={lista}
                detalle={(e) => (e.lote ? `Lote ${e.lote.numero} · ${e.lote.nombre}` : null)}
              />
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
}

function ListaNombres({ titulo, personas, detalle }) {
  const [todas, setTodas] = useState(false);
  if (!personas.length) return null;
  const visibles = todas ? personas : personas.slice(0, MAX_NOMBRES);
  return (
    <div>
      <div className="text-xs font-medium text-v25-ink-700 mb-1">{titulo} ({personas.length})</div>
      <ul className="text-sm text-v25-ink-700 grid sm:grid-cols-2 gap-x-4">
        {visibles.map((p) => (
          <li key={`${p.persona_id}`} className="truncate">
            {p.nombre || `#${p.persona_id}`}
            {detalle && detalle(p) && <span className="text-xs text-v25-ink-400"> — {detalle(p)}</span>}
          </li>
        ))}
      </ul>
      {personas.length > MAX_NOMBRES && (
        <button type="button" onClick={() => setTodas((v) => !v)} className="text-xs text-v25-ink-500 underline mt-1">
          {todas ? 'Ver menos' : `Ver las ${personas.length}`}
        </button>
      )}
    </div>
  );
}
